import React, { useState, useEffect, useRef } from 'react';
import './WhatsAppButton.css';

const WhatsAppButton = () => {
  const [showChatbox, setShowChatbox] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const timerRef = useRef(null);
  const secondMessageSentRef = useRef(false);
  const phoneNumber = '+971555592206';
  const message = encodeURIComponent("Hi *KD General Transport*! I need more info about your transportation services.");

  // Predefined messages to send
  const autoMessages = [
    "Welcome to KD General Transport! We provide professional transportation services across UAE including Freezer Vans, Chiller Trucks, Movers, and Trailers.",
    "How can we assist you with your transportation needs today?"
  ];

  const playNotificationSound = () => {
    try {
      // Create audio context for notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Softer, more pleasant notification sound (like WhatsApp)
      oscillator.type = 'sine'; // Softer than default
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5 note
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5 note
      oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5 note

      // Softer volume with smooth fade
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } catch (error) {
      // Silently fail if audio context is not available
      console.log('Audio context not available');
    }
  };

  useEffect(() => {
    // Show first notification with first message after 15 seconds
    const timer1 = setTimeout(() => {
      setMessageCount(1);
      setDisplayedMessages([autoMessages[0]]);
      playNotificationSound();
    }, 15000);

    return () => {
      clearTimeout(timer1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // autoMessages is a constant array, no need to include in deps

  // Handle second message when user clicks WhatsApp icon
  useEffect(() => {
    // Only trigger when chatbox opens, we have exactly 1 message, and haven't sent second message yet
    if (showChatbox && displayedMessages.length === 1 && !secondMessageSentRef.current) {
      // Mark as sent immediately to prevent duplicate
      secondMessageSentRef.current = true;
      
      // Clear any existing timer first
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      // Show typing indicator
      setIsTyping(true);
      
      // Show second message after 2 seconds
      timerRef.current = setTimeout(() => {
        setIsTyping(false);
        setDisplayedMessages([autoMessages[0], autoMessages[1]]);
        setMessageCount(2);
        playNotificationSound();
      }, 2000);
    }
    
    // If both messages already exist, make sure typing is off
    if (displayedMessages.length === 2) {
      setIsTyping(false);
    }
    
    // Cleanup function
    return () => {
      if (timerRef.current && !showChatbox) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChatbox, displayedMessages.length]); // autoMessages is a constant array, no need to include in deps

  const handleButtonClick = () => {
    const newState = !showChatbox;
    setShowChatbox(newState);
    // Don't reset badge when opening/closing - keep it visible
    // Don't reset messages when closing - keep them for next time
    if (!newState) {
      setIsTyping(false);
    }
  };

  const handleCloseChatbox = () => {
    setShowChatbox(false);
    setIsTyping(false);
    // Don't reset messages - keep them displayed
  };

  const handleOpenWhatsApp = () => {
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
    setShowChatbox(false);
    setMessageCount(0);
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div
      className={`joinchat joinchat--right ${showChatbox ? 'joinchat--show' : ''}`}
      aria-hidden={!showChatbox}
    >
      {/* WhatsApp Button */}
      <div
        className="joinchat__button"
        role="button"
        tabIndex={0}
        onClick={handleButtonClick}
        onKeyDown={(e) => handleKeyDown(e, handleButtonClick)}
        aria-label="Open chat"
      />

      {/* Badge Counter - Hide when chatbox is open */}
      {messageCount > 0 && !showChatbox && (
        <div className="joinchat__badge">{messageCount}</div>
      )}

      {/* Chatbox */}
      <div
        className="joinchat__chatbox"
        role="dialog"
        aria-labelledby="joinchat__label"
        aria-modal="true"
      >
        {/* Header */}
        <div className="joinchat__header">
          <div id="joinchat__label">
            <span>Welcome To KD General Transport</span>
          </div>
          <div
            className="joinchat__close"
            role="button"
            tabIndex={0}
            onClick={handleCloseChatbox}
            onKeyDown={(e) => handleKeyDown(e, handleCloseChatbox)}
            aria-label="Close"
          />
        </div>

        {/* Scrollable Content */}
        <div className="joinchat__scroll">
          <div className="joinchat__content">
            {/* Chat Messages */}
            <div className="joinchat__chat">
              {displayedMessages.map((msg, index) => (
                <div key={index} className="joinchat__bubble">
                  {msg}
                </div>
              ))}
              
              {/* Typing Indicator */}
              {isTyping && (
                <div className="joinchat__typing">
                  <div className="joinchat__typing-dot"></div>
                  <div className="joinchat__typing-dot"></div>
                  <div className="joinchat__typing-dot"></div>
                </div>
              )}
            </div>

            {/* Open Chat Button */}
            <div className="joinchat__footer">
              <button
                className="joinchat__send"
                onClick={handleOpenWhatsApp}
                aria-label="Open chat"
              >
                <span>Open Chat</span>
                <svg
                  className="joinchat__send-icon"
                  width={20}
                  height={20}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppButton;

